import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NOTIFICATION_COUNT_LIMIT,
  type Notification,
  type NotificationLoadResult,
} from './notifications.types';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { ModulesService } from 'app/api/services/modules.service';
import { SignalRService } from 'app/core/services/signalr.service';
import { UserStore } from 'app/core/services/user.store';
import type { Entry } from 'app/features/admin/modules/modules.model';
import type { EditEntry } from 'app/features/admin/modules/features/entries/entries.model';
import { composeEntriesFilter } from 'app/features/admin/modules/features/entries/entries-filter.util';
import { SKIP_HTTP_CACHE } from 'app/shared/interceptors/caching.interceptor';
import { isMessageNotification } from './notifications.util';
import { debounceTime, filter, take } from 'rxjs';

const MARK_ALL_WRITE_CONCURRENCY = 4;
const UNREAD_COUNT_QUERY_LIMIT = NOTIFICATION_COUNT_LIMIT + 1;
const UNREAD_COUNT_REFRESH_DEBOUNCE_MS = 100;

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  readonly modulesStore = inject(ModulesStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modulesService = inject(ModulesService);
  readonly #signalRService = inject(SignalRService);
  readonly userStore = inject(UserStore);

  readonly moduleId = computed(() => this.modulesStore.moduleCodeToId('notifications'));
  readonly entity = computed(() => this.modulesStore.enabledEntities().find(item => item.id === this.moduleId()));
  readonly loading = signal(false);
  readonly loadMoreFailed = signal(false);
  readonly unreadCountLoading = signal(false);
  readonly unreadCountFailed = signal(false);
  readonly markingAllAsRead = signal(false);
  readonly markAllAsReadFailed = signal(false);
  readonly pageSize = NOTIFICATION_COUNT_LIMIT;

  readonly entries = computed<Notification[]>(() => {
    const entity = this.entity();
    if (entity && this.modulesStore.entries()[entity.id] !== undefined) {
      return this.modulesStore.entries()[entity.id].map((entry: Notification) => ({
        ...entry,
        module: this.modulesStore.getModuleById(entry.moduleId),
      }));
    }
    return [];
  });
  readonly #serverUnreadCount = signal<number | null>(null);
  readonly unreadCount = computed(
    () => this.#serverUnreadCount() ?? this.entries().filter(notification => !notification.entry.read).length
  );
  readonly hasMoreUnreadEntries = computed(() => this.#serverUnreadCount() === UNREAD_COUNT_QUERY_LIMIT);
  readonly messageCount = computed(() => this.entries().filter(isMessageNotification).length);
  readonly hasMoreEntries = computed(() => {
    const moduleId = this.moduleId();
    return Boolean(
      moduleId &&
      this.modulesStore.entriesLoaded()[moduleId] &&
      this.modulesStore.entriesPageLength()[moduleId] === undefined
    );
  });

  readonly #pageIndex = signal(0);
  #failedPageIndex: number | null = null;
  #unreadCountRequestVersion = 0;
  #unreadCountScope: string | null = null;
  #markAllCutoff: number | null = null;
  #markAllInitiallyLoadedEntryIds = new Set<string>();

  constructor() {
    this.#signalRService.entryChanged$
      .pipe(
        filter(({ moduleId }) => moduleId === this.moduleId()),
        debounceTime(UNREAD_COUNT_REFRESH_DEBOUNCE_MS),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.refreshUnreadCount());
  }

  markAllAsRead(): void {
    if (this.markingAllAsRead() || this.loading()) {
      return;
    }

    this.loadMoreFailed.set(false);
    this.markAllAsReadFailed.set(false);
    this.markingAllAsRead.set(true);
    const loadedEntries = this.entries();
    this.#markAllInitiallyLoadedEntryIds = new Set(loadedEntries.map(entry => entry.id));
    const validTimestamps = loadedEntries
      .map(entry => this.notificationTimestamp(entry))
      .filter((timestamp): timestamp is number => timestamp !== null);
    this.#markAllCutoff = validTimestamps.reduce<number | null>(
      (latestTimestamp, timestamp) => (latestTimestamp === null ? timestamp : Math.max(latestTimestamp, timestamp)),
      null
    );
    this.loadAllEntriesAndMarkAsRead();
  }

  private markLoadedEntriesAsRead(): void {
    const unreadEntries = this.entries().filter(
      (entry: Entry) => !entry.entry.read && this.existedWhenMarkAllStarted(entry)
    );
    this.persistReadEntries(unreadEntries);
  }

  private persistReadEntries(unreadEntries: Entry[], startIndex = 0): void {
    const batch = unreadEntries.slice(startIndex, startIndex + MARK_ALL_WRITE_CONCURRENCY);
    if (batch.length === 0) {
      this.markingAllAsRead.set(false);
      this.refreshUnreadCount();
      return;
    }

    let pendingWrites = batch.length;
    let batchFailureCount = 0;
    const settleWrite = (failed: boolean) => {
      if (failed) {
        batchFailureCount++;
      }
      pendingWrites--;
      if (pendingWrites === 0) {
        if (batchFailureCount > 0) {
          this.markingAllAsRead.set(false);
          this.markAllAsReadFailed.set(true);
          this.refreshUnreadCount();
          return;
        }
        this.persistReadEntries(unreadEntries, startIndex + MARK_ALL_WRITE_CONCURRENCY);
      }
    };

    batch.forEach((entry: Entry) => {
      const updatedEntry: EditEntry = {
        entry: {
          ...entry,
          entry: {
            ...entry.entry,
            read: true,
          },
        },
        entryId: entry.id,
        moduleId: entry.moduleId,
        rollbackOnError: true,
        showSnackBarMessage: false,
        suppressErrorNotification: true,
        onSuccess: () => settleWrite(false),
        onError: () => settleWrite(true),
      };
      this.modulesStore.editEntry(updatedEntry);
    });
  }

  private existedWhenMarkAllStarted(entry: Entry): boolean {
    if (this.#markAllInitiallyLoadedEntryIds.has(entry.id)) {
      return true;
    }

    const timestamp = this.notificationTimestamp(entry);
    return timestamp !== null && this.#markAllCutoff !== null && timestamp <= this.#markAllCutoff;
  }

  private notificationTimestamp(entry: Entry): number | null {
    const notificationTime = entry.entry.time;
    if (!notificationTime) {
      return null;
    }

    const timestamp = notificationTime instanceof Date ? notificationTime.getTime() : new Date(notificationTime).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  toggleRead(entry: Entry): void {
    const updatedEntry = {
      entry: {
        ...entry,
        entry: {
          ...entry.entry,
          read: !entry.entry.read,
        },
      },
      entryId: entry.id,
      moduleId: entry.moduleId,
      rollbackOnError: true,
      showSnackBarMessage: false,
      onSuccess: () => this.refreshUnreadCount(),
      onError: () => this.refreshUnreadCount(),
    };
    this.modulesStore.editEntry(updatedEntry);
  }

  refreshUnreadCount(): void {
    const moduleId = this.moduleId();
    const userId = this.userStore.currentUserId();
    if (!moduleId || !userId) {
      this.#unreadCountRequestVersion++;
      this.#unreadCountScope = null;
      this.#serverUnreadCount.set(null);
      this.unreadCountLoading.set(false);
      this.unreadCountFailed.set(false);
      return;
    }

    const scope = `${moduleId}:${userId}`;
    if (this.#unreadCountScope !== scope) {
      this.#unreadCountScope = scope;
      this.#serverUnreadCount.set(null);
    }

    const requestVersion = ++this.#unreadCountRequestVersion;
    const escapedUserId = userId.replaceAll("'", "''");
    this.unreadCountLoading.set(true);
    this.unreadCountFailed.set(false);

    this.#modulesService
      .getModuleEntries(
        {
          moduleId,
          $filter: composeEntriesFilter(`entry.user.id eq '${escapedUserId}'`, 'entry.read ne true'),
          $skip: '0',
          $top: UNREAD_COUNT_QUERY_LIMIT.toString(),
          $small: true,
        },
        new HttpContext().set(SKIP_HTTP_CACHE, true)
      )
      .pipe(take(1))
      .subscribe({
        next: entries => {
          if (requestVersion !== this.#unreadCountRequestVersion) {
            return;
          }
          this.#serverUnreadCount.set(Math.min(entries.length, UNREAD_COUNT_QUERY_LIMIT));
          this.unreadCountLoading.set(false);
        },
        error: () => {
          if (requestVersion !== this.#unreadCountRequestVersion) {
            return;
          }
          this.unreadCountLoading.set(false);
          this.unreadCountFailed.set(true);
        },
      });
  }

  loadEntries(reset = false, onSettled?: (result: NotificationLoadResult) => void): void {
    const entity = this.entity();
    if (!entity || this.loading()) {
      onSettled?.('skipped');
      return;
    }

    if (reset) {
      this.#pageIndex.set(0);
      this.loadMoreFailed.set(false);
      this.#failedPageIndex = null;
      this.refreshUnreadCount();
    }

    const userIdFilter = "entry.user.id eq '" + this.userStore.currentUserId() + "'";
    this.loading.set(true);
    this.modulesStore.loadEntries({
      moduleId: entity.id,
      offset: this.#pageIndex() * this.pageSize,
      limit: this.pageSize,
      filter: userIdFilter,
      orderBy: { active: 'entry.time', direction: 'desc' },
      applyQueryParams: false,
      forceReload: reset,
      onSettled: result => {
        this.loading.set(false);
        if (result === 'error') {
          this.#failedPageIndex = this.#pageIndex();
          this.loadMoreFailed.set(true);
        } else if (result === 'loaded') {
          this.#failedPageIndex = null;
          this.loadMoreFailed.set(false);
        }
        onSettled?.(result);
      },
    });
  }

  loadMoreEntries(event: Event): void {
    const element = event.target;
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom > 2) {
      return;
    }

    this.loadNextPage();
  }

  loadNextPage(onSettled?: (result: NotificationLoadResult) => void): void {
    if (this.loading() || this.loadMoreFailed() || !this.hasMoreEntries()) {
      onSettled?.('skipped');
      return;
    }

    const moduleId = this.moduleId();
    const previousPageIndex = this.#pageIndex();
    const nextPageIndex = this.#pageIndex() + 1;
    const nextOffset = nextPageIndex * this.pageSize;
    const lastPageOffset = moduleId ? this.modulesStore.entriesPageLength()[moduleId] : undefined;

    if (lastPageOffset !== undefined && lastPageOffset < nextOffset) {
      onSettled?.('skipped');
      return;
    }

    this.#pageIndex.set(nextPageIndex);
    this.loadEntries(false, result => {
      if (result === 'error') {
        this.#pageIndex.set(previousPageIndex);
        this.loadMoreFailed.set(true);
      } else if (result === 'loaded') {
        this.loadMoreFailed.set(false);
      }
      onSettled?.(result);
    });
  }

  retryLoadMoreEntries(): void {
    const failedPageIndex = this.#failedPageIndex;
    if (failedPageIndex === null || this.loading()) {
      return;
    }

    this.loadMoreFailed.set(false);
    this.#failedPageIndex = null;
    if (failedPageIndex === 0) {
      this.#pageIndex.set(0);
      this.loadEntries();
      return;
    }

    this.#pageIndex.set(failedPageIndex - 1);
    this.loadNextPage();
  }

  private loadAllEntriesAndMarkAsRead(): void {
    if (!this.hasMoreEntries()) {
      this.markLoadedEntriesAsRead();
      return;
    }

    this.loadNextPage(result => {
      if (result === 'error') {
        this.markingAllAsRead.set(false);
        this.markAllAsReadFailed.set(true);
        return;
      }

      this.loadAllEntriesAndMarkAsRead();
    });
  }
}
